import { type ReactNode } from 'react';
import {
  FIELD_TYPES,
  type FieldDefinition,
  type FieldType,
  type FieldValue,
  type RenderedValue,
} from '@whinerp/contracts/fields';
import { Icon, type IconName } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';
import { cx } from '../primitives/cx.js';

/**
 * One renderer per field type, in one registry.
 *
 * The registry is typed as `Record<FieldType, FieldRenderer>`, so adding a
 * type to `FIELD_TYPES` is a compile error here until it has a renderer. A
 * missing renderer would otherwise be a blank cell, which is the worst
 * possible outcome: it looks exactly like a field with no value.
 *
 * Renderers do not format. The plan arrives with `rendered` already computed
 * by the one formatter, and these choose the shape - a chip, a link, a
 * checkmark. A renderer that formatted its own number would be a second answer
 * to a question the formatter already answered (P-1).
 */

export interface FieldRendererLabels {
  /** For a boolean: what true and false are called. */
  yes: string;
  no: string;
  /** For a file: the download affordance's accessible name. */
  download: string;
  /** For a multipicklist or tags with more values than fit. */
  more: (count: number) => string;
  /** For an empty editable field. */
  choose: string;
  clear: string;
}

export interface DisplayProps {
  field: FieldDefinition;
  rendered: RenderedValue;
  labels: FieldRendererLabels;
  /** Long text and addresses render taller; a table cell says no. */
  compact?: boolean;
}

export interface EditProps {
  field: FieldDefinition;
  value: FieldValue;
  labels: FieldRendererLabels;
  onChange: (next: FieldValue) => void;
  /** Set when this field is in conflict; the input is outlined and described. */
  invalid?: boolean;
  describedBy?: string | undefined;
  id: string;
}

export interface FieldRenderer {
  Display: (props: DisplayProps) => JSX.Element;
  Edit: (props: EditProps) => JSX.Element;
  /** Whether the value is naturally numeric, so the cell can align it. */
  numeric?: boolean;
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

function AbsentValue({ rendered }: { rendered: RenderedValue }): JSX.Element {
  if (rendered.kind !== 'absent') return <></>;
  return (
    <span
      data-absent={rendered.absent.reason}
      className={cx(
        'text-body',
        // "Hidden" is a different fact from "not set" and reads differently.
        rendered.absent.reason === 'hidden' ? 'italic text-ink-400' : 'text-ink-300',
      )}
    >
      {rendered.absent.text}
    </span>
  );
}

/** Every Display starts here, so absence is handled once and consistently. */
function withAbsence(render: (rendered: RenderedValue) => ReactNode) {
  return function Display({ rendered }: DisplayProps): JSX.Element {
    if (rendered.kind === 'absent') return <AbsentValue rendered={rendered} />;
    return <>{render(rendered)}</>;
  };
}

function textOf(rendered: RenderedValue): string {
  return rendered.kind === 'text' ? rendered.text : '';
}

function TextInput({
  id,
  value,
  onChange,
  invalid,
  describedBy,
  type = 'text',
  inputMode,
}: EditProps & { type?: string; inputMode?: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url' }): JSX.Element {
  return (
    <input
      id={id}
      type={type}
      inputMode={inputMode}
      value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(event) => onChange(event.target.value)}
      className={cx(
        'h-input w-full rounded-control border bg-surface px-3 text-body text-ink',
        'focus-visible:outline-none',
        invalid ? 'border-danger' : 'border-rule',
      )}
    />
  );
}

const plainText: FieldRenderer = {
  Display: withAbsence((r) => <span className="text-body text-ink">{textOf(r)}</span>),
  Edit: (props) => <TextInput {...props} />,
};

function linkish(href: (raw: string) => string, icon: IconName): FieldRenderer {
  return {
    Display: withAbsence((r) => (
      <a
        href={href(textOf(r))}
        className="inline-flex items-center gap-1 text-body text-bronze-text underline decoration-bronze-rule underline-offset-4"
      >
        <Icon name={icon} size="inline" decorative />
        {textOf(r)}
      </a>
    )),
    Edit: (props) => <TextInput {...props} inputMode={icon === 'Envelope' ? 'email' : 'tel'} />,
  };
}

function numberish(inputMode: 'numeric' | 'decimal'): FieldRenderer {
  return {
    numeric: true,
    Display: withAbsence((r) => (
      <Num align="end" className="text-body text-ink">
        {textOf(r)}
      </Num>
    )),
    Edit: (props) => <TextInput {...props} inputMode={inputMode} />,
  };
}

function OptionChips({ rendered, labels, compact }: DisplayProps): JSX.Element {
  if (rendered.kind !== 'options') return <AbsentValue rendered={rendered} />;
  const shown = compact ? rendered.values.slice(0, 2) : rendered.values;
  const rest = rendered.values.length - shown.length;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {shown.map((option) => (
        <span
          key={option.value}
          data-option={option.value}
          className={cx(
            'inline-flex h-chip items-center rounded-chip px-2 text-meta',
            option.tone === 'success' && 'bg-success-tint text-success-text',
            option.tone === 'warning' && 'bg-warning-tint text-warning-text',
            option.tone === 'danger' && 'bg-danger-tint text-danger-text',
            option.tone === 'info' && 'bg-info-tint text-info-text',
            (!option.tone || option.tone === 'neutral') && 'bg-sunken text-ink-600',
          )}
        >
          {option.label}
        </span>
      ))}
      {rest > 0 ? <span className="text-meta text-ink-meta">{labels.more(rest)}</span> : null}
    </span>
  );
}

function Select({ field, value, onChange, id, invalid, describedBy, labels }: EditProps): JSX.Element {
  return (
    <select
      id={id}
      value={typeof value === 'string' ? value : ''}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      className={cx(
        'h-input w-full rounded-control border bg-surface px-3 text-body text-ink',
        'focus-visible:outline-none',
        invalid ? 'border-danger' : 'border-rule',
      )}
    >
      <option value="">{field.required ? labels.choose : labels.clear}</option>
      {(field.options ?? []).map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------

export const FIELD_RENDERERS: Record<FieldType, FieldRenderer> = {
  text: plainText,

  textarea: {
    Display: withAbsence((r) => (
      <span className="whitespace-pre-wrap text-body text-ink">{textOf(r)}</span>
    )),
    Edit: ({ id, value, onChange, invalid, describedBy }) => (
      <textarea
        id={id}
        rows={4}
        value={typeof value === 'string' ? value : ''}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
        className={cx(
          'w-full rounded-control border bg-surface px-3 py-2 text-body text-ink',
          'focus-visible:outline-none',
          invalid ? 'border-danger' : 'border-rule',
        )}
      />
    ),
  },

  email: linkish((raw) => `mailto:${raw}`, 'Envelope'),
  phone: linkish((raw) => `tel:${raw.replace(/[^+0-9]/g, '')}`, 'Phone'),
  url: linkish((raw) => (/^https?:/.test(raw) ? raw : `https://${raw}`), 'ArrowSquareOut'),

  number: numberish('numeric'),
  decimal: numberish('decimal'),
  currency: numberish('decimal'),
  percent: numberish('decimal'),

  date: {
    Display: withAbsence((r) => <Num className="text-body text-ink">{textOf(r)}</Num>),
    Edit: (props) => <TextInput {...props} type="date" />,
  },

  datetime: {
    Display: withAbsence((r) => <Num className="text-body text-ink">{textOf(r)}</Num>),
    Edit: (props) => <TextInput {...props} type="datetime-local" />,
  },

  boolean: {
    Display: ({ rendered, labels }) => {
      if (rendered.kind !== 'boolean') return <AbsentValue rendered={rendered} />;
      return (
        <span className="inline-flex items-center gap-1 text-body text-ink">
          <Icon name={rendered.value ? 'Check' : 'Minus'} size="inline" decorative />
          {rendered.value ? labels.yes : labels.no}
        </span>
      );
    },
    Edit: ({ id, value, onChange, describedBy }) => (
      <input
        id={id}
        type="checkbox"
        checked={value === true}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 rounded-control border border-rule accent-bronze"
      />
    ),
  },

  picklist: { Display: OptionChips, Edit: Select },

  multipicklist: {
    Display: OptionChips,
    Edit: ({ field, value, onChange, id, describedBy }) => {
      const chosen = Array.isArray(value) ? value : [];
      return (
        <fieldset id={id} aria-describedby={describedBy} className="flex flex-wrap gap-2">
          {(field.options ?? []).map((option) => (
            <label key={option.value} className="inline-flex items-center gap-1 text-dense text-ink">
              <input
                type="checkbox"
                checked={chosen.includes(option.value)}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...chosen, option.value]
                      : chosen.filter((v) => v !== option.value),
                  )
                }
                className="h-4 w-4 rounded-control border border-rule accent-bronze"
              />
              {option.label}
            </label>
          ))}
        </fieldset>
      );
    },
  },

  tags: {
    Display: OptionChips,
    Edit: ({ id, value, onChange, describedBy }) => (
      <input
        id={id}
        type="text"
        value={Array.isArray(value) ? value.join(', ') : ''}
        aria-describedby={describedBy}
        onChange={(event) =>
          onChange(
            event.target.value
              .split(',')
              .map((part) => part.trim())
              .filter(Boolean),
          )
        }
        className="h-input w-full rounded-control border border-rule bg-surface px-3 text-body text-ink focus-visible:outline-none"
      />
    ),
  },

  lookup: {
    Display: ({ rendered, field }) => {
      if (rendered.kind !== 'lookup') return <AbsentValue rendered={rendered} />;
      const { value } = rendered;
      // A lookup carries its name. A screen holding only an id would have to
      // go and ask for one, and a UUID must never reach a person's eyes.
      return value.href ? (
        <a
          href={value.href}
          data-lookup={field.key}
          className="text-body text-bronze-text underline decoration-bronze-rule underline-offset-4"
        >
          {value.name}
        </a>
      ) : (
        <span className="text-body text-ink">{value.name}</span>
      );
    },
    Edit: ({ id, value, onChange, labels, describedBy }) => {
      const current = value && typeof value === 'object' && 'name' in value ? value.name : '';
      // A real lookup picker arrives with the record screens; until it does
      // this shows the current value and offers to clear it, and says so,
      // rather than pretending to search.
      return (
        <div className="flex items-center gap-2" id={id} aria-describedby={describedBy}>
          <span className="text-body text-ink">{current || labels.choose}</span>
          {current ? (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-meta text-ink-meta underline underline-offset-4"
            >
              {labels.clear}
            </button>
          ) : null}
        </div>
      );
    },
  },

  user: {
    Display: ({ rendered }) => {
      if (rendered.kind !== 'lookup') return <AbsentValue rendered={rendered} />;
      return (
        <span className="inline-flex items-center gap-2 text-body text-ink">
          <span
            aria-hidden
            className="flex h-6 w-6 items-center justify-center rounded-chip bg-sunken text-meta text-ink-600"
          >
            {initials(rendered.value.name)}
          </span>
          {rendered.value.name}
        </span>
      );
    },
    Edit: ({ id, value, labels, describedBy }) => (
      <span id={id} aria-describedby={describedBy} className="text-body text-ink">
        {value && typeof value === 'object' && 'name' in value ? value.name : labels.choose}
      </span>
    ),
  },

  file: {
    Display: ({ rendered, labels }) => {
      if (rendered.kind !== 'file') return <AbsentValue rendered={rendered} />;
      return (
        <a
          href={`/files/${rendered.value.id}`}
          aria-label={`${labels.download}: ${rendered.value.name}`}
          className="inline-flex items-center gap-1 text-body text-bronze-text underline decoration-bronze-rule underline-offset-4"
        >
          <Icon name="Paperclip" size="inline" decorative />
          {rendered.value.name}
        </a>
      );
    },
    Edit: ({ id, describedBy, onChange }) => (
      <input
        id={id}
        type="file"
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.files?.[0]?.name ?? null)}
        className="text-dense text-ink"
      />
    ),
  },

  address: {
    Display: ({ rendered }) => {
      if (rendered.kind !== 'address') return <AbsentValue rendered={rendered} />;
      const a = rendered.value;
      const lines = [a.line1, a.line2, [a.city, a.region].filter(Boolean).join(' '), a.postcode, a.country]
        .map((line) => (line ?? '').trim())
        .filter(Boolean);
      return (
        <span className="flex flex-col text-body text-ink">
          {lines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </span>
      );
    },
    Edit: ({ id, value, onChange, describedBy }) => {
      const a = (value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<
        string,
        string | null | undefined
      >;
      const part = (key: string, label: string): JSX.Element => (
        <input
          key={key}
          type="text"
          aria-label={label}
          value={a[key] ?? ''}
          onChange={(event) => onChange({ ...a, [key]: event.target.value })}
          className="h-input w-full rounded-control border border-rule bg-surface px-3 text-body text-ink focus-visible:outline-none"
        />
      );
      return (
        <div id={id} aria-describedby={describedBy} className="grid grid-cols-2 gap-2">
          {part('line1', 'Line 1')}
          {part('line2', 'Line 2')}
          {part('city', 'City')}
          {part('region', 'Region')}
          {part('postcode', 'Postcode')}
          {part('country', 'Country')}
        </div>
      );
    },
  },

  formula: {
    numeric: true,
    Display: withAbsence((r) => (
      <span className="inline-flex items-center gap-1 text-body text-ink">
        <Icon name="Sigma" size="inline" decorative />
        <Num>{textOf(r)}</Num>
      </span>
    )),
    // A derived field is never editable, and the planner already refuses to
    // mark it so. This exists because the registry must be total, and it says
    // what it is rather than rendering an input that would do nothing.
    Edit: ({ id }) => <DerivedNotEditable id={id} />,
  },

  rollup: {
    numeric: true,
    Display: withAbsence((r) => (
      <span className="inline-flex items-center gap-1 text-body text-ink">
        <Icon name="Sigma" size="inline" decorative />
        <Num>{textOf(r)}</Num>
      </span>
    )),
    Edit: ({ id }) => <DerivedNotEditable id={id} />,
  },
};

function DerivedNotEditable({ id }: { id: string }): JSX.Element {
  return (
    <span id={id} data-derived className="text-body text-ink-400">
      <Icon name="Sigma" size="inline" decorative />
    </span>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/** Every type has a renderer. Derived from the type list, not written out. */
export const RENDERED_FIELD_TYPES: readonly FieldType[] = FIELD_TYPES;
